import { useQuery } from "@tanstack/react-query";
import type React from "react";
import { useEffect, useState } from "react";

import type { DocumentsItemDto } from "@/api/types";
import { type Breadcrumb, Breadcrumbs } from "@/components/Breadcrumbs/Breadcrumbs";
import { Button } from "@/components/Button/Button";
import { useFlashToast } from "@/components/FlashToast/FlashToast";
import { FullSizeLoader } from "@/components/FullSizeLoader/FullSizeLoader";
import { Modal, type ModalBaseProps } from "@/components/Modal/Modal";
import { documentMutations, useDocumentQueries } from "@/queries/documents";
import { useTranslation } from "@/translations";

import { DocumentsFileIcon } from "./DocumentsFileIcon";

type DocumentsMoveItemModalProps = ModalBaseProps & {
  item: DocumentsItemDto | undefined;
  folderId: string;
};

const ROOT_FOLDER_ID = "root";

export function DocumentsMoveItemModal({
  isOpened,
  onOpenChange,
  item,
  folderId,
}: DocumentsMoveItemModalProps): React.ReactNode {
  const [currentFolderId, setCurrentFolderId] = useState(folderId);

  const { t } = useTranslation();
  const showFlashToast = useFlashToast();
  const isRootFolder = currentFolderId === ROOT_FOLDER_ID;

  useEffect(() => {
    setCurrentFolderId(folderId);
  }, [folderId]);

  const documentQueries = useDocumentQueries();
  const {
    data: documentList,
    isLoading: isLoadingDocumentList,
    error: documentListError,
  } = useQuery({
    ...documentQueries.listDocuments({
      folderId: currentFolderId,
    }),
    enabled: !isRootFolder && isOpened,
  });
  const {
    data: rootList,
    isLoading: isLoadingRootList,
    error: rootListError,
  } = useQuery({
    ...documentQueries.listRoots(),
    enabled: isRootFolder && isOpened,
  });
  const {
    data: fileDetails,
    isLoading: isLoadingFileDetails,
    error: fileDetailsError,
  } = useQuery({
    ...documentQueries.fileDetaills({
      folderId,
      fileId: item?.id || "-",
    }),
    enabled: item != null && item.type !== "folder" && isOpened,
  });
  const {
    data: folderDetails,
    isLoading: isLoadingFolderDetails,
    error: folderDetailsError,
  } = useQuery({
    ...documentQueries.folderDetails({
      folderId: item?.id || "-",
    }),
    enabled: item != null && item.type === "folder" && isOpened,
  });
  const updateFolderMutation = documentMutations.useUpdateFolder();
  const updateFileMutation = documentMutations.useUpdateFile();

  const handleSubmit = async () => {
    if (item!.type === "folder") {
      await updateFolderMutation.mutateAsync({
        id: item!.id,
        data: {
          nameTranslations: folderDetails!.nameTranslations,
          parentFolderId: currentFolderId,
        },
      });
    } else {
      await updateFileMutation.mutateAsync({
        id: item!.id,
        data: {
          nameTranslations: fileDetails!.nameTranslations,
          parentFolderId: currentFolderId,
          uploadId: item!.document!.id,
        },
      });
    }

    onOpenChange(false);
  };

  const error = rootListError || documentListError || fileDetailsError || folderDetailsError;

  useEffect(() => {
    if (item != null && error) {
      showFlashToast({
        title: t("component.documents.move-item-modal.notification.error"),
        type: "error",
      });

      onOpenChange(false);
    }
  }, [error, item, onOpenChange, showFlashToast, t]);

  const items =
    (isRootFolder ? rootList?.filter((f) => f.canAddItem) : documentList?.items.filter((x) => x.type === "folder")) ??
    [];

  const breadcrumbs = [
    {
      name: t("model.document.breadcrumbs.root"),
      id: ROOT_FOLDER_ID,
    },
    ...(documentList?.ancestors || []),
    ...(!isRootFolder && documentList
      ? [
          {
            name: documentList.name,
            id: currentFolderId,
          },
        ]
      : []),
  ]
    .filter((x) => x)
    .map(
      ({ name, id: currFolderId }) =>
        ({
          callback: () => setCurrentFolderId(currFolderId),
          name,
        }) satisfies Breadcrumb,
    );

  const isSubmitting = updateFileMutation.isPending || updateFolderMutation.isPending;
  const isLoading = isLoadingRootList || isLoadingDocumentList || isLoadingFolderDetails || isLoadingFileDetails;

  return (
    <Modal.Root
      isOpened={item != null}
      onOpenChange={onOpenChange}
      title={t("component.documents.move-item-modal.title")}
      size="sm"
      data-testid="documents-move-modal"
    >
      {item && (
        <div className="flex items-center gap-2">
          <div className="shrink-0">
            <DocumentsFileIcon type={item.type} />
          </div>
          <span className="text-caption">{item.name}</span>
        </div>
      )}
      <hr className="mb-4 mt-2 h-px border-0 bg-grey-300" />
      <Breadcrumbs pages={breadcrumbs} />
      <div className="my-4 min-h-52 rounded-3px border border-grey-300 p-4">
        {isLoading && <FullSizeLoader />}
        {!isLoading && items.length === 0 && (
          <span className="text-caption text-grey-500">{t("component.documents.move-item-modal.list-empty")}</span>
        )}
        {!isLoading && items.length > 0 && (
          <ul className="flex flex-col gap-1.5">
            {items.map(({ id, name }) => (
              <li key={id}>
                <button className="flex items-center gap-2" onClick={() => setCurrentFolderId(id)}>
                  <div className="shrink-0">
                    <DocumentsFileIcon type="folder" />
                  </div>
                  <span className="text-caption">{name}</span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </div>
      <Modal.Actions>
        <Modal.Close>
          <Button styling="secondary">{t("common.action.cancel")}</Button>
        </Modal.Close>
        <Button
          data-testid="documents-move-modal-submit"
          onClick={handleSubmit}
          isLoading={isSubmitting}
          disabled={!isRootFolder && currentFolderId === folderId}
        >
          {t("component.documents.move-item-modal.action.move")}
        </Button>
      </Modal.Actions>
    </Modal.Root>
  );
}
